import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { localIgnored } from "../utils/local-ignored"
import { useAuthContext } from "./AuthContext"

const IgnoredContext = React.createContext(null)

function startIgnored() {
  return localIgnored.load()
}

function sameList(a, b) {
  return a.length === b.length && a.every((n, i) => n === b[i])
}

export const IgnoredContextProvider = ({ children }) => {
  // runtime-only toggle; defaults to false and is not persisted
  const [ignoreEnabled, setIgnoreEnabled] = useState(false)
  // list of ignored card names (strings); persisted via local storage
  // when logged out, via the account doc when logged in
  const [ignoredList, setIgnoredList] = useState(startIgnored)

  const authCtx = useAuthContext()
  const user = authCtx?.user
  const cloudDoc = authCtx?.cloudDoc
  const cloudLoaded = authCtx?.cloudLoaded
  const appliedFromCloudRef = useRef(false)

  useEffect(() => {
    if (!user || !Array.isArray(cloudDoc?.ignored)) {
      return
    }
    // a queued-but-unflushed local change is newer than this snapshot
    // (a server ack racing the debounce window) — don't revert it
    if (authCtx.getSync()?.pendingFields().ignored) {
      return
    }
    setIgnoredList((curr) => {
      if (sameList(curr, cloudDoc.ignored)) {
        return curr
      }
      appliedFromCloudRef.current = true
      return cloudDoc.ignored
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cloudDoc])

  // on logout the device returns to its own local list
  const prevUserRef = useRef(null)
  useEffect(() => {
    if (prevUserRef.current && !user) {
      setIgnoredList(startIgnored())
    }
    prevUserRef.current = user
  }, [user])

  const persistUserRef = useRef(null)
  useEffect(() => {
    // auth just flipped: the list still holds the other mode's data,
    // persisting it would cross the streams — skip this render
    if (persistUserRef.current !== user) {
      persistUserRef.current = user
      return
    }
    if (!user) {
      localIgnored.store(ignoredList)
      return
    }
    // never push before the first snapshot: the device's stale list
    // would wholesale replace the account's cloud copy
    if (!cloudLoaded) {
      return
    }
    if (appliedFromCloudRef.current) {
      appliedFromCloudRef.current = false
      return
    }
    authCtx.getSync()?.queueField("ignored", ignoredList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoredList, user, cloudLoaded])

  // Expose as a Set for O(1) membership checks in consumers
  const ignoredSet = useMemo(() => new Set(ignoredList), [ignoredList])

  const toggleIgnore = useCallback(() => {
    setIgnoreEnabled((b) => !b)
  }, [])

  const addToIgnored = useCallback((name) => {
    if (typeof name !== "string" || !name) return
    setIgnoredList((list) => (list.includes(name) ? list : [...list, name]))
  }, [])

  const removeFromIgnored = useCallback((name) => {
    setIgnoredList((list) => list.filter((n) => n !== name))
  }, [])

  const clearIgnored = useCallback(() => {
    setIgnoredList([])
  }, [])

  return (
    <IgnoredContext.Provider
      value={{
        ignoreEnabled,
        ignoredSet,
        ignoredList,
        toggleIgnore,
        addToIgnored,
        removeFromIgnored,
        clearIgnored,
      }}
    >
      {children}
    </IgnoredContext.Provider>
  )
}

export const useIgnoredContext = () => {
  const value = useContext(IgnoredContext)
  if (!value) throw new Error("Called useIgnoredContext() outside provider")
  return value
}
